
import { GuideStep as GuideStepType } from "@/hooks/use-guide-mode";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Check, Wrench, Pencil, Ruler, RotateCw, Scissors, LucideIcon } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { motion } from "framer-motion";

interface GuideStepProps {
  step: GuideStepType;
  progress: {
    current: number;
    total: number;
  };
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  isStepCompleted: boolean;
  onToolSelect?: (tool: string) => void;
  isExpanded?: boolean;
  onAnalyze?: () => void;
}

const toolIcons: Record<string, LucideIcon> = {
  draw: Pencil,
  ruler: Ruler,
  angle: RotateCw,
  incision: Scissors,
  scalpel: Scissors
};

export const GuideStep = ({
  step,
  progress,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  isStepCompleted,
  onToolSelect,
  isExpanded = false,
  onAnalyze
}: GuideStepProps) => {
  const [animateCheck, setAnimateCheck] = useState(false);
  
  useEffect(() => {
    if (isStepCompleted) {
      setAnimateCheck(true);
      const timer = setTimeout(() => setAnimateCheck(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [isStepCompleted]);
  
  const handleToolClick = () => {
    if (step.tool && onToolSelect) {
      onToolSelect(step.tool);
    }
  };
  
  const handleNextClick = () => {
    if (isLastStep && onAnalyze) {
      onAnalyze();
    } else {
      onNext();
    }
  };
  
  const StepIcon = step.tool ? toolIcons[step.tool] || Wrench : Wrench;
  
  return (
    <div className={cn(
      "space-y-3",
      isExpanded && "space-y-4"
    )}>
      <Progress 
        value={progress.current / progress.total * 100} 
        className="h-1 bg-gray-100/30" 
      />

      <div className={cn(
        "bg-white/40 backdrop-blur-sm rounded-lg border border-gray-200/40 p-4 space-y-2", // Reduced opacity to 40%
        isExpanded && "space-y-3 p-5"
      )}>
        <div className="flex items-center justify-between gap-2">
          <h2 className={cn(
            "font-medium text-gray-800",
            isExpanded ? "text-base" : "text-xs"
          )}>
            {step.title}
          </h2>
          
          {step.tool && (
            <Badge 
              variant="outline" 
              className="bg-gray-50/60 text-gray-700 border-gray-200/60 cursor-pointer text-[10px] px-1.5 py-0.5" 
              onClick={handleToolClick}
            >
              <StepIcon className="h-2.5 w-2.5 mr-1" />
              {step.tool === 'scalpel' ? 'Scalpel' : step.tool.charAt(0).toUpperCase() + step.tool.slice(1)}
            </Badge>
          )}
        </div>
        
        {step.imageUrl && (
          <div className={cn(
            "rounded-md overflow-hidden border border-gray-200/40",
            isExpanded ? "mb-3" : "mb-2"
          )}>
            <AspectRatio ratio={16 / 9}>
              <img 
                src={step.imageUrl} 
                alt={step.title} 
                className="w-full h-full object-cover" 
                onError={e => {
                  console.error("Failed to load image:", step.imageUrl);
                  e.currentTarget.style.display = 'none';
                }}
              />
            </AspectRatio>
          </div>
        )}
        
        <p className={cn(
          "text-gray-700 leading-relaxed whitespace-pre-wrap",
          isExpanded ? "text-xs" : "text-[11px]"
        )}>
          {step.description}
        </p>
      </div>

      <div className="flex items-center justify-between pt-1">
        <Button 
          variant="outline" 
          size="sm"
          onClick={onPrevious} 
          disabled={isFirstStep} 
          className={cn(
            "gap-1 border-gray-200/60 text-gray-700 bg-white/40 hover:bg-white/60 text-xs h-7 px-2",
            isFirstStep && "opacity-50 cursor-not-allowed"
          )}
        >
          <ChevronLeft className="h-3 w-3" />
          Previous
        </Button>

        <Button 
          variant={isLastStep ? "default" : "default"} 
          size="sm"
          onClick={handleNextClick} 
          className={cn(
            "gap-1 text-xs h-7 px-2",
            isLastStep ? "bg-indigo-600/80 hover:bg-indigo-700" : "bg-indigo-500/80 hover:bg-indigo-600/90"
          )}
        >
          {isLastStep ? "Analyze" : "Next"}
          {!isLastStep && <ChevronRight className="h-3 w-3" />}
        </Button>
      </div>
    </div>
  );
};
